import { useEffect, useState } from "react";
import {
  ApiError,
  confirmCa,
  fetchCaReviewQueue,
  fetchReconciliationQueue,
  fetchUnmatchedMappingsQueue,
  linkRename,
  rejectCa,
  resolveDemerger,
  saveCorrectedMapping,
  type CaReviewRowDto,
  type ReconciliationRowDto,
  type UnmatchedMappingRowDto,
} from "../api";
import { FieldHelp } from "../components/FieldHelp";
import { PageHelper } from "../components/PageHelper";
import { exportRowsAsCsv } from "../exportCsv";
import { sortGlyph, useSort } from "../hooks/useSort";

type Tab = "ca" | "rec" | "unm";

/** Name/ticker PRIMARY, ISIN secondary for identification -- e.g. "RELIANCE (INE002A01018)".
 * Falls back to the ISIN alone when unmatched: an honest gap, never a fabricated name.
 * Used everywhere a security is referenced on this page, including inside
 * window.confirm/window.prompt text for the CA-confirm action -- the one
 * place on this page where an admin restates real prices and must be able
 * to tell which company they're confirming before clicking through. */
function nameFor(securityId: string, displayTicker?: string | null, companyName?: string | null): string {
  if (displayTicker) return `${displayTicker} (${securityId})`;
  if (companyName) return `${companyName} (${securityId})`;
  return securityId;
}

function fmtRatio(v: number | string | null): string {
  // Defensive: the backend now sends a real JSON number (fixed at the
  // source -- app/api/routes/queues.py casts the NUMERIC columns to
  // float8), but this coercion is cheap insurance against the exact bug
  // that crashed the whole page before that fix -- a future numeric-as-
  // string regression degrades to "—" instead of unmounting the app.
  if (v === null || v === undefined) return "—";
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n.toFixed(4) : "—";
}

export function QueuesPage({ token, onUnauthorized }: { token: string; onUnauthorized: () => void }) {
  const [tab, setTab] = useState<Tab>("ca");
  const [caRows, setCaRows] = useState<CaReviewRowDto[] | null>(null);
  const [caTotal, setCaTotal] = useState(0);
  const [recRows, setRecRows] = useState<ReconciliationRowDto[] | null>(null);
  const [recTotal, setRecTotal] = useState(0);
  const [unmRows, setUnmRows] = useState<UnmatchedMappingRowDto[] | null>(null);
  const [unmTotal, setUnmTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [actionNote, setActionNote] = useState<string | null>(null);
  const [mappingDraft, setMappingDraft] = useState<Record<number, { securityId: string; symbol: string }>>({});

  function handleAuthAwareError(err: unknown): boolean {
    if (err instanceof ApiError && err.status === 401) {
      onUnauthorized();
      return true;
    }
    return false;
  }

  function reload() {
    Promise.all([
      fetchCaReviewQueue(token, 100, 0),
      fetchReconciliationQueue(token, 100, 0),
      fetchUnmatchedMappingsQueue(token, 100, 0),
    ])
      .then(([ca, rec, unm]) => {
        setCaRows(ca.rows);
        setCaTotal(ca.total_pending);
        setRecRows(rec.rows);
        setRecTotal(rec.total_pending);
        setUnmRows(unm.rows);
        setUnmTotal(unm.total_pending);
      })
      .catch((err) => {
        if (!handleAuthAwareError(err)) setError(err instanceof ApiError ? err.message : "request failed");
      });
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function handleConfirmCa(row: CaReviewRowDto) {
    const proposed = row.observed_ratio ?? row.yf_ratio;
    const who = nameFor(row.security_id, row.display_ticker, row.company_name);
    const ok = window.confirm(
      `Confirm this corporate action for ${who} (ex-date ${row.ex_date})?\n\n` +
      `This RESTATES every adjusted price before the ex-date for this security (proposed ratio ≈ ${proposed ?? "?"}), ` +
      `and rebuilds every stored feature for it. This cannot be undone from the UI.\n\n` +
      `Proceed?`,
    );
    if (!ok) return;
    setBusyId(row.queue_id);
    setActionNote(null);
    try {
      const result = await confirmCa(token, row.queue_id);
      setActionNote(`Confirmed ${nameFor(result.security_id, result.display_ticker, result.company_name)} ${result.ex_date}: factor=${result.adjustment_factor} (${result.note})`);
      reload();
    } catch (err) {
      if (!handleAuthAwareError(err)) setActionNote(`Confirm failed: ${err instanceof ApiError ? err.message : "request failed"}`);
    } finally {
      setBusyId(null);
    }
  }

  async function handleRejectCa(row: CaReviewRowDto) {
    const who = nameFor(row.security_id, row.display_ticker, row.company_name);
    const reason = window.prompt(`Reject the CA candidate for ${who} (${row.ex_date})? Optional reason:`, "");
    if (reason === null) return;
    setBusyId(row.queue_id);
    setActionNote(null);
    try {
      const result = await rejectCa(token, row.queue_id, reason || undefined);
      setActionNote(`Rejected ${nameFor(result.security_id, result.display_ticker, result.company_name)} ${result.ex_date} -- no restatement applied.`);
      reload();
    } catch (err) {
      if (!handleAuthAwareError(err)) setActionNote(`Reject failed: ${err instanceof ApiError ? err.message : "request failed"}`);
    } finally {
      setBusyId(null);
    }
  }

  async function handleLinkRename(row: ReconciliationRowDto) {
    const who = nameFor(row.security_id ?? row.isin, row.display_ticker, row.company_name);
    const newValue = window.prompt(`Confirm the new ${row.code_type} for ${who} (rename):`, row.new_value ?? "");
    if (!newValue) return;
    setBusyId(row.queue_id);
    setActionNote(null);
    try {
      const result = await linkRename(token, row.queue_id, newValue);
      setActionNote(`Linked as rename: ${result.old_value} -> ${result.new_value} on ${result.security_id}.`);
      reload();
    } catch (err) {
      if (!handleAuthAwareError(err)) setActionNote(`Link-rename failed: ${err instanceof ApiError ? err.message : "request failed"}`);
    } finally {
      setBusyId(null);
    }
  }

  async function handleDemerger(row: ReconciliationRowDto) {
    const who = nameFor(row.security_id ?? row.isin, row.display_ticker, row.company_name);
    const newValue = window.prompt(`Record the spun-off ${row.code_type} for ${who} (demerger -- parent's own code is NOT changed):`, row.new_value ?? "");
    if (!newValue) return;
    setBusyId(row.queue_id);
    setActionNote(null);
    try {
      const result = await resolveDemerger(token, row.queue_id, newValue);
      setActionNote(`Recorded as demerger: ${result.old_value} -> ${result.new_value} on ${result.security_id} (security_master unchanged).`);
      reload();
    } catch (err) {
      if (!handleAuthAwareError(err)) setActionNote(`Demerger resolve failed: ${err instanceof ApiError ? err.message : "request failed"}`);
    } finally {
      setBusyId(null);
    }
  }

  function draftFor(queueId: number, fallbackSecurityId: string) {
    return mappingDraft[queueId] ?? { securityId: fallbackSecurityId, symbol: "" };
  }

  async function handleSaveMapping(row: UnmatchedMappingRowDto) {
    const draft = draftFor(row.queue_id, row.isin);
    if (!draft.securityId || !draft.symbol) {
      setActionNote("Save failed: security_id and corrected symbol are both required.");
      return;
    }
    setBusyId(row.queue_id);
    setActionNote(null);
    try {
      const result = await saveCorrectedMapping(token, row.queue_id, draft.securityId, draft.symbol);
      setActionNote(`Saved ${result.target_source} symbol ${result.corrected_symbol} for ${result.security_id}.`);
      reload();
    } catch (err) {
      if (!handleAuthAwareError(err)) setActionNote(`Save failed: ${err instanceof ApiError ? err.message : "request failed"}`);
    } finally {
      setBusyId(null);
    }
  }

  const { sorted: sortedCaRows, sortKey: caSortKey, sortDir: caSortDir, toggleSort: toggleCaSort } = useSort<CaReviewRowDto>(
    (caRows ?? []).slice(0, 100),
    (row, key) => (row as unknown as Record<string, unknown>)[key],
  );

  function exportCaCsv() {
    exportRowsAsCsv(
      (caRows ?? []) as unknown as Record<string, unknown>[],
      [
        { key: "security_id", label: "Security" },
        { key: "ex_date", label: "Ex-date" },
        { key: "action_type", label: "Type" },
        { key: "yf_ratio", label: "yf ratio" },
        { key: "observed_ratio", label: "Observed" },
        { key: "classification", label: "Classification" },
        { key: "notes", label: "Check" },
      ],
      "ca_review_queue.csv",
    );
  }

  if (error) return <div className="error" style={{ margin: 14 }}>{error}</div>;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Review queues</h1>
          <div className="page-sub">Admin confirmations before data enters the store. Confirming a corporate action restates that security's history — act deliberately.</div>
        </div>
      </div>

      <PageHelper>
        Three real admin queues awaiting confirmation before data enters the store: <FieldHelp term="corporate_action" />
        corporate actions, ticker-mapping reconciliation, and unmatched symbols. Confirming a CA is the most
        consequential action in the app — it restates the security's adjusted price history and rebuilds its
        stored features. Nothing here is auto-applied; every action is one deliberate click plus a confirmation.
      </PageHelper>

      {actionNote && (
        <div className="banner show" style={{ display: "flex" }}>
          <span>{actionNote.startsWith("Save failed") || actionNote.includes("failed") ? "⚠" : "✓"}</span>
          <span>{actionNote}</span>
        </div>
      )}

      <div className="banner show" style={{ display: "flex" }}>
        <span>ⓘ</span>
        <span>
          Every write here goes through <code>app/reads/ca_confirm.py</code>'s existing, already-tested confirm/
          reject machinery (CA-review) or writes a real <code>code_history</code>/<code>security_master</code> row
          (reconciliation/mappings) — real writes with real, cascading effects. A confirm cannot be undone from
          this UI.
        </span>
      </div>

      <div className="levtabs" style={{ margin: "0 0 16px" }}>
        <button className={tab === "ca" ? "on" : undefined} onClick={() => setTab("ca")}>
          Corporate actions <span className="gaps-badge" style={{ marginLeft: 6 }}>{caTotal.toLocaleString()}</span>
        </button>
        <button className={tab === "rec" ? "on" : undefined} onClick={() => setTab("rec")}>
          Reconciliation <span className="gaps-badge" style={{ marginLeft: 6 }}>{recTotal.toLocaleString()}</span>
        </button>
        <button className={tab === "unm" ? "on" : undefined} onClick={() => setTab("unm")}>
          Unmatched mappings <span className="gaps-badge" style={{ marginLeft: 6 }}>{unmTotal.toLocaleString()}</span>
        </button>
      </div>

      {tab === "ca" && (
        <div className="card" style={{ padding: 0 }}>
          <div className="results-bar">
            <div className="results-left">
              <span className="results-count"><b>{caTotal.toLocaleString()}</b> pending</span>
              <span className="note">Magnitude comes from the observed price cliff, not the yfinance ratio — disagreements flagged.</span>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button className="btn ghost sm" onClick={exportCaCsv}>↓ CSV</button>
            </div>
          </div>
          <div className="table-wrap" style={{ maxHeight: 520, overflow: "auto" }}>
            <table className="stocks-table">
              <thead>
                <tr>
                  <th className="l clickable" onClick={() => toggleCaSort("security_id")}>Security {sortGlyph("security_id", caSortKey, caSortDir)}</th>
                  <th className="clickable" onClick={() => toggleCaSort("ex_date")}>Ex-date {sortGlyph("ex_date", caSortKey, caSortDir)}</th>
                  <th className="clickable" onClick={() => toggleCaSort("action_type")}>Type <FieldHelp term="corporate_action" /> {sortGlyph("action_type", caSortKey, caSortDir)}</th>
                  <th className="r clickable" onClick={() => toggleCaSort("yf_ratio")}>yf ratio {sortGlyph("yf_ratio", caSortKey, caSortDir)}</th>
                  <th className="r clickable" onClick={() => toggleCaSort("observed_ratio")}>Observed {sortGlyph("observed_ratio", caSortKey, caSortDir)}</th>
                  <th className="clickable" onClick={() => toggleCaSort("classification")}>Classification <FieldHelp term="adjustment_factor" /> {sortGlyph("classification", caSortKey, caSortDir)}</th>
                  <th>Check</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedCaRows.map((r) => (
                  <tr key={r.queue_id}>
                    <td className="l">
                      <div style={{ fontWeight: 600 }}>{r.display_ticker ?? r.security_id}</div>
                      <div className="mut mono" style={{ fontSize: "0.72rem" }}>
                        {r.display_ticker ? r.security_id : (r.company_name ?? "unmatched ISIN")}
                      </div>
                    </td>
                    <td className="mono" style={{ fontSize: "0.78rem" }}>{r.ex_date}</td>
                    <td>{r.action_type ? <span className="chip">{r.action_type}</span> : <span className="mut">—</span>}</td>
                    <td className="r mono">{fmtRatio(r.yf_ratio)}</td>
                    <td className="r mono" style={{ color: r.ratios_disagree ? "var(--bad)" : undefined }}>{fmtRatio(r.observed_ratio)}</td>
                    <td>{r.classification ? <span className="chip">{r.classification}</span> : <span className="mut">—</span>}</td>
                    <td style={{ maxWidth: 260, fontSize: "0.76rem", color: "var(--ink-soft)" }}>{r.notes}</td>
                    <td>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button
                          className="btn ghost sm" disabled={busyId === r.queue_id}
                          style={{ borderColor: "var(--good, #2a7)", color: "var(--good, #2a7)" }}
                          onClick={() => handleConfirmCa(r)}
                        >
                          {busyId === r.queue_id ? "…" : "✓ Confirm"}
                        </button>
                        <button
                          className="btn ghost sm" disabled={busyId === r.queue_id}
                          onClick={() => handleRejectCa(r)}
                        >
                          {busyId === r.queue_id ? "…" : "✕ Reject"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="foot-note">Showing up to 100 of {caTotal.toLocaleString()} pending rows — export/pagination not built this session.</div>
        </div>
      )}

      {tab === "rec" && (
        <div className="card" style={{ padding: 0 }}>
          <div className="qhead" style={{ padding: 14 }}>
            <div className="desc">A code vanished from today&apos;s master and a new unmapped code appeared. Same ISIN = rename (link, history carries); different identity = demerger (attribute pre-event history).</div>
          </div>
          {recRows && recRows.length === 0 ? (
            <div className="empty-state">
              <div className="headline">No pending reconciliation events</div>
              <div>reconciliation_queue has 0 rows right now — genuinely empty, not a loading state or a bug.</div>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="stocks-table">
                <thead><tr><th className="l">Security</th><th className="l">Old code</th><th className="l">New code</th><th>ISIN</th><th>Event type</th><th>Actions</th></tr></thead>
                <tbody>
                  {(recRows ?? []).map((r) => (
                    <tr key={r.queue_id}>
                      <td className="l">
                        <div style={{ fontWeight: 600 }}>{r.display_ticker ?? r.security_id ?? r.isin}</div>
                        {r.display_ticker && <div className="mut" style={{ fontSize: "0.72rem" }}>{r.company_name ?? r.security_id}</div>}
                      </td>
                      <td className="l mono" style={{ fontWeight: 600 }}>{r.old_value}</td>
                      <td className="l mono" style={{ fontWeight: 600 }}>{r.new_value}</td>
                      <td className="mono">{r.isin}</td>
                      <td>{r.event_type ? <span className="chip">{r.event_type}</span> : <span className="mut">—</span>}</td>
                      <td>
                        <div style={{ display: "flex", gap: 4 }}>
                          <button className="btn ghost sm" disabled={busyId === r.queue_id} onClick={() => handleLinkRename(r)}>
                            {busyId === r.queue_id ? "…" : "Link rename"}
                          </button>
                          <button className="btn ghost sm" disabled={busyId === r.queue_id} onClick={() => handleDemerger(r)}>
                            {busyId === r.queue_id ? "…" : "Demerger…"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "unm" && (
        <div className="card" style={{ padding: 0 }}>
          <div className="results-bar">
            <div className="results-left">
              <span className="results-count"><b>{unmTotal.toLocaleString()}</b> pending</span>
              <span className="note">Universe names whose yfinance symbol didn&apos;t verify. security_id defaults to ISIN — override if this row belongs to a different security.</span>
            </div>
          </div>
          <div className="table-wrap" style={{ maxHeight: 520, overflow: "auto" }}>
            <table className="stocks-table">
              <thead><tr><th className="l">ISIN</th><th>Company</th><th>Attempted symbol</th><th>Target source</th><th>Reason</th><th>Corrected symbol</th><th>Actions</th></tr></thead>
              <tbody>
                {(unmRows ?? []).slice(0, 100).map((r) => {
                  const draft = draftFor(r.queue_id, r.isin);
                  return (
                    <tr key={r.queue_id}>
                      <td className="l mono" style={{ fontWeight: 600 }}>{r.isin}</td>
                      <td>{r.company_name ?? r.display_ticker ?? <span className="mut">—</span>}</td>
                      <td className="mono">{r.attempted_symbol ?? <span className="mut">—</span>}</td>
                      <td><span className="chip">{r.target_source}</span></td>
                      <td style={{ fontSize: "0.78rem", color: "var(--ink-soft)" }}>{r.reason}</td>
                      <td>
                        <input
                          type="text" placeholder="corrected symbol" value={draft.symbol}
                          style={{ fontSize: "0.76rem", padding: "3px 5px", width: 130 }}
                          onChange={(e) => setMappingDraft((prev) => ({ ...prev, [r.queue_id]: { ...draft, symbol: e.target.value } }))}
                        />
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: 4 }}>
                          <button className="btn ghost sm" disabled={busyId === r.queue_id} onClick={() => handleSaveMapping(r)}>
                            {busyId === r.queue_id ? "…" : "✓ Save"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="foot-note">Showing up to 100 of {unmTotal.toLocaleString()} pending rows.</div>
        </div>
      )}
    </>
  );
}
